/* Brooklyn Sells sign-up pop-up (sections/bs-signup.liquid). */

if (!customElements.get('bs-signup')) {
  const DONE_KEY = 'bsSignupDone';
  const DISMISSED_KEY = 'bsSignupDismissedAt';

  const store = {
    get(key) {
      try {
        return localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (error) {}
    },
  };

  class BsSignup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.bs-signup__dialog');
      this.input = this.querySelector('.bs-signup__input');
      this.isOpen = false;

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-bs-signup-close]')) this.close();
        const copy = event.target.closest('[data-bs-signup-copy]');
        if (copy) this.copyCode(copy);
      });
      this.addEventListener('keydown', (event) => this.onKeydown(event));

      const form = this.querySelector('form');
      if (form && this.input) form.addEventListener('submit', (event) => this.onSubmit(event));

      // Back from a submit: show the thank-you or the error straight away.
      if (this.querySelector('[data-bs-signup-success]')) {
        store.set(DONE_KEY, '1');
        this.open();
        return;
      }
      if (this.querySelector('[data-bs-signup-error]')) {
        this.open();
        return;
      }

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:select', (event) => {
          if (this.closest('.shopify-section') && event.target === this.closest('.shopify-section')) this.open();
        });
        document.addEventListener('shopify:section:deselect', () => this.close(true));
        return;
      }

      if (!this.shouldAutoOpen()) return;
      this.armTriggers();
    }

    shouldAutoOpen() {
      if (store.get(DONE_KEY)) return false;
      const dismissedAt = parseInt(store.get(DISMISSED_KEY), 10);
      const days = parseInt(this.dataset.days, 10) || 7;
      if (dismissedAt && Date.now() - dismissedAt < days * 864e5) return false;
      if (this.dataset.mobile !== 'true' && window.matchMedia('(max-width: 749px)').matches) return false;
      return true;
    }

    armTriggers() {
      const delay = (parseInt(this.dataset.delay, 10) || 20) * 1000;
      this.timer = setTimeout(() => this.tryOpen(true), delay);

      const scrollShare = (parseInt(this.dataset.scroll, 10) || 50) / 100;
      this.onScroll = () => {
        const room = document.documentElement.scrollHeight - window.innerHeight;
        if (room > 0 && window.scrollY / room >= scrollShare) this.tryOpen(true);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });

      if (this.dataset.exit === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.onLeave = (event) => {
          if (!event.relatedTarget && event.clientY <= 0) this.tryOpen(false);
        };
        document.addEventListener('mouseout', this.onLeave);
      }
    }

    disarm() {
      clearTimeout(this.timer);
      clearTimeout(this.retry);
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
    }

    // Don't interrupt: wait while the sidebar, cart, a quick view or the
    // cookie banner is open, or while the shopper is typing.
    isBusy() {
      if (document.querySelector('header-drawer details[open], cart-drawer.active, .quick-add-modal[open], modal-dialog[open], details[open] > .modal__content')) return true;
      const cookie = document.querySelector("[data-button-type='accept_button']");
      if (cookie && cookie.offsetParent !== null) return true;
      const active = document.activeElement;
      return !!(active && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName));
    }

    tryOpen(canWait) {
      if (this.isOpen) return;
      if (this.isBusy()) {
        if (canWait) {
          clearTimeout(this.retry);
          this.retry = setTimeout(() => this.tryOpen(true), 4000);
        }
        return;
      }
      this.disarm();
      this.open();
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.lastFocus = document.activeElement;
      this.hidden = false;
      document.body.classList.add('bs-signup-open');
      requestAnimationFrame(() => this.classList.add('is-open'));
      const target = this.querySelector('[data-bs-signup-error]') ? this.input : this.dialog;
      setTimeout(() => (target || this.dialog).focus({ preventScroll: true }), 50);
    }

    close(silent) {
      if (!this.isOpen) return;
      this.isOpen = false;
      if (!silent && !store.get(DONE_KEY)) store.set(DISMISSED_KEY, String(Date.now()));
      this.classList.remove('is-open');
      document.body.classList.remove('bs-signup-open');
      setTimeout(() => {
        if (!this.isOpen) this.hidden = true;
      }, 250);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        this.dialog.querySelectorAll('button, input:not([type="hidden"]), a[href]')
      ).filter((el) => !el.disabled && el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onSubmit(event) {
      if (this.input.checkValidity() && this.input.value.trim()) return;
      event.preventDefault();
      let error = this.querySelector('[data-bs-signup-error]');
      if (!error) {
        error = document.createElement('p');
        error.id = 'BsSignupError';
        error.className = 'bs-signup__error';
        error.setAttribute('data-bs-signup-error', '');
        this.input.closest('.bs-signup__row').after(error);
      }
      error.textContent = 'Enter a valid email address, like you@example.com.';
      this.input.setAttribute('aria-invalid', 'true');
      this.input.setAttribute('aria-describedby', error.id);
      this.input.focus();
    }

    copyCode(button) {
      const code = button.getAttribute('data-bs-signup-copy');
      const done = () => {
        button.textContent = 'Copied';
        setTimeout(() => (button.textContent = 'Copy code'), 2000);
      };
      if (navigator.clipboard) navigator.clipboard.writeText(code).then(done, () => {});
    }
  }

  customElements.define('bs-signup', BsSignup);
}
